import $ from 'jquery';
import { isLocalStorageSupported } from '../../utils';

const countsAjax = {
  myFilters: null,
  category: null,
  customField: null,
};

export const setupFilters = body => {
  const $body = $(body);

  // setupFilters is called on both BEFORE_UNLOAD (to auto-expand filters before body swap, preventing flicker)
  // and CHANGE (standard init) with same body. Early return prevents duplicate AJAX requests and wasted work.
  if ($body.data('setup-filters-called')) {
    return;
  }
  $body.data('setup-filters-called', true);

  // Abort any in-flight requests from previous page
  for (const key in countsAjax) {
    if (countsAjax[key] && countsAjax[key].readyState !== 4) {
      countsAjax[key].abort();
    }
  }

  // Load counts for the my ideas area
  const $myFilters = $body.find('.filters--my-filters');
  const loadMyFilterCountsUrl = $myFilters.data('load-counts-url');

  if ($myFilters.length > 0 && loadMyFilterCountsUrl) {
    // Only show one spinner and only if it's going to take awhile
    const timer = setTimeout(() => {
      const $element = $body.find('[data-my-ideas-count]');
      if ($element.text().trim() === '') {
        $element.html('<aha-spinner />');
      }
    }, 500);

    countsAjax.myFilters = $.get(loadMyFilterCountsUrl)
      .done(data => {
        $body.find('[data-my-ideas-count]').text(data.my_ideas_count);
        $body.find('[data-my-votes-count]').text(data.my_votes_count);
        $body
          .find('[data-my-proxy-votes-count]')
          .text(data.my_proxy_votes_count);
        $body.find('[data-proxy-votes-count]').text(data.proxy_votes_count);
        $body
          .find('[data-my-org-votes-count]')
          .text(data.my_org_votes_ideas_count);
        $body
          .find('[data-my-subscriptions-count]')
          .text(data.my_subscriptions_count);
      })
      .always(() => {
        clearTimeout(timer);
        $body.find('[data-my-ideas-count] aha-spinner').remove();
      });
  }

  // Load counts for the collapsable filters (categories and custom fields)
  const $collapsableFilter = $body.find('.collapsable-filter');
  if ($collapsableFilter.length === 0) {
    return;
  }

  // There are two collapsable filters with counts: the category filter (which includes projects and categories),
  // and the custom field filter (which is just custom field options).
  const countsLoaded = { category: false, customField: false };

  // For saving expand/collapse state per filter per portal. So the key includes
  // the portal id and the filter container id (custom field or category).
  const localStorageKey = container => {
    const portalId = $body.find('meta[name="idea-portal-id"]').attr('content');
    return `portal-${portalId}-${container.attr('id')}-state`;
  };

  // Derive project line counts as the sum of their descendant project counts
  const setProjectLineCounts = $container => {
    setTimeout(() => {
      $container.find('.count--project-line').each((_, element) => {
        const $this = $(element);

        $this.text(
          $this
            .closest('[data-filter-type="project"]')
            .find('.count--project') // projects only, not lines, avoids double counting
            .toArray()
            .reduce((acc, el) => acc + parseInt($(el).text(), 10) || 0, 0)
        );
      });
    }, 200);
  };

  // Click to expand/collapse sidebar filter. For custom field filter and category filter.
  $collapsableFilter
    .find('.expand-collapse')
    .rebind('click.collapsable-filter', event => {
      const $container = $(event.target).closest('.collapsable-filter');
      const $toggle = $container.find('.expand-collapse');
      const $filters = $container.find('.filters');
      const containerId = $container.attr('id');
      const isCategoryFilter = containerId === 'category-filter';
      const filterType = isCategoryFilter ? 'category' : 'customField';
      const loadCountsUrl = $container.data('load-counts-url');

      $filters.toggleClass('hidden');
      const expanded = !$filters.hasClass('hidden');

      $toggle.find('i').toggleClass('fa-chevron-down fa-chevron-right');
      $toggle.attr('aria-expanded', expanded);

      // Lazy load the counts on expand first time
      if (expanded && loadCountsUrl && !countsLoaded[filterType]) {
        const timer = setTimeout(() => {
          $container
            .find('.filter:not(.active):first .count')
            .html('<aha-spinner />');
        }, 500);

        countsAjax[filterType] = $.get(loadCountsUrl)
          .done(data => {
            countsLoaded[filterType] = true;
            $container.find('.count').empty();

            if (isCategoryFilter) {
              // Populate counts for category tree: project counts, category counts, and project line counts
              $.each(data.project_counts, function (id, count) {
                $container.find(`#project-${id} .count--project`).text(count);
              });
              $.each(data.category_counts, function (id, count) {
                $container
                  .find(`#category-${id} .category-count:first`)
                  .text(count);
              });
              setProjectLineCounts($container); // based on project counts established above
            } else {
              // Populate the custom field option counts
              $.each(data, function (id, count) {
                const selector = `#custom_field_option_${id} .cfo-count`;
                $container.find(selector).text(count);
              });
            }

            $container.find('.count:empty').text('0');
          })
          .always(() => {
            clearTimeout(timer);
            $container.find('.count aha-spinner').remove();
          });
      }

      // Save the expand/collapse state for this filter container
      if (isLocalStorageSupported()) {
        localStorage.setItem(
          localStorageKey($container),
          expanded ? 'expanded' : 'collapsed'
        );
      }
    });

  // Activate a sidebar filter (category filter or custom field filter). Shows it as active, and replaces
  // its count with a close icon.
  const activateFilter = selector => {
    const $filter = $body.find(selector);

    if ($filter.length > 0) {
      $filter.find('.filter:first').addClass('active');
      $filter.find('.aha-icon-close:first').removeClass('hide');
      $filter.find('.count:first').addClass('hide');
    }
  };

  // On page load, activate the selected filters, and auto-expand the filter container.
  // For custom field filter and category filter.
  $.each($collapsableFilter, (_, container) => {
    const $container = $(container);
    const filteredProjectIds = $container.data('filtered-project-ids');
    const filteredCategoryIds = $container.data('filtered-category-ids');
    const filteredCfoId = $container.data('filtered-cfo-id');

    filteredProjectIds?.forEach(projectId => {
      activateFilter(`#project-${projectId}`);
    });
    filteredCategoryIds?.forEach(categoryId => {
      activateFilter(`#category-${categoryId}`);
    });
    if (filteredCfoId) {
      activateFilter(`#cfo-${filteredCfoId}`);
    }

    const isCollapsed = $container.find('.filters').hasClass('hidden');
    const hasActiveFilter = $container.find('.filter.active').length > 0;
    const state = isLocalStorageSupported()
      ? localStorage.getItem(localStorageKey($container))
      : null;
    const isCategoryFilter = $container.attr('id') === 'category-filter';
    const shouldBeExpanded =
      state === 'expanded' ||
      hasActiveFilter ||
      (isCategoryFilter && state === null);

    if (isCollapsed && shouldBeExpanded) {
      $container.find('.expand-collapse').click();
    }
  });

  // Derive project line counts on page load when not lazy loading
  if (!$body.find('#category-filter').data('load-counts-url')) {
    setProjectLineCounts($body);
  }
};
